import { Button, FAIcon } from '@/components'
import { useConfirm } from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { GenerationModifierDialog } from './generation-modifier-dialog'
import type {
  GenerationModifierAward,
  GenerationModifierChainAction,
  GenerationModifierLocationTest,
  GenerationModifierMatchMode,
  GenerationModifierSubject,
  GenerationRuleKind,
  GenerationRuleRounding,
  RestType,
} from './generation-rule-labels'
import { useGenerationRuleLabels } from './generation-rule-labels'
import { DELETE_SALARY_ITEM_GENERATION_MODIFIER_MUTATION } from './mutations'
import type { GenerationModifierNode } from './queries'
import {
  ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY,
  DIET_SETTINGS_SALARY_WORK_TIMES_QUERY,
} from './queries'
import { groupModifiers } from './generation-modifier-groups'
import { BetaChip } from '../generation-preview/beta-chip'
import { countryName } from '@/utils/country-name'

export const GenerationModifiersSection: React.FC = () => {
  const translations = useTranslate({
    title: 'settings.generation-modifiers.title',
    description: 'settings.generation-modifiers.description',
    groupEmpty: 'settings.generation-modifiers.group-empty',
    companyWide: 'settings.generation-modifiers.company-wide',
    deletePrompt: 'settings.generation-modifiers.delete-prompt',
    inactive: 'settings.generation-rules.inactive',
    delete: 'common.delete',
  })
  const confirm = useConfirm()

  const { data } = useQuery(ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY)
  const { data: salaryWorkTimeData } = useQuery(
    DIET_SETTINGS_SALARY_WORK_TIMES_QUERY
  )
  const groups = groupModifiers(
    data?.allSalaryItemGenerationModifiers ?? [],
    salaryWorkTimeData?.allSalaryWorkTimes ?? [],
    translations.companyWide
  )

  const [deleteModifier] = useMutation(
    DELETE_SALARY_ITEM_GENERATION_MODIFIER_MUTATION,
    { refetchQueries: [{ query: ALL_SALARY_ITEM_GENERATION_MODIFIERS_QUERY }] }
  )

  async function handleDelete(modifier: GenerationModifierNode) {
    const { data: confirmed } = await confirm(
      translations.deletePrompt,
      undefined,
      { confirmText: translations.delete }
    )
    if (confirmed) await deleteModifier({ variables: { id: modifier.id } })
  }

  return (
    <section className="flex flex-col gap-3 overflow-hidden rounded-2xl bg-white px-5 py-4 shadow-card">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-stone-900">
          {translations.title}
        </h2>
        <BetaChip />
      </div>
      <p className="text-sm text-stone-600">{translations.description}</p>

      {groups.map(group => (
        <div
          key={group.salaryWorkTimeId ?? 'company'}
          className="-mx-5 flex flex-col gap-3 border-t border-stone-200 px-5 pt-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-stone-900">{group.name}</h3>
            <GenerationModifierDialog
              salaryWorkTimeId={group.salaryWorkTimeId ?? undefined}
            />
          </div>
          {group.modifiers.length === 0 ? (
            <p className="text-sm text-stone-600">{translations.groupEmpty}</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {group.modifiers.map(modifier => (
                <li
                  key={modifier.id}
                  className="flex items-center justify-between gap-4 rounded-lg border border-stone-200 p-3"
                >
                  <GenerationModifierSummary modifier={modifier} />
                  <div className="flex shrink-0 items-center gap-1">
                    {!modifier.isActive && (
                      <span className="text-xs text-stone-500">
                        {translations.inactive}
                      </span>
                    )}
                    <GenerationModifierDialog modifier={modifier} />
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => void handleDelete(modifier)}
                    >
                      <FAIcon icon="trash" />
                    </Button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </section>
  )
}

const GenerationModifierSummary: React.FC<{
  modifier: GenerationModifierNode
}> = ({ modifier }) => {
  const labels = useGenerationRuleLabels()
  const translations = useTranslate({
    priority: 'common.priority',
    hours: 'common.hours-abbrev',
    from: 'common.from',
    until: 'common.until',
    per: 'settings.generation-modifiers.per',
    max: 'settings.generation-modifiers.summary-max',
    minDuration: 'settings.generation-modifiers.summary-min-duration',
    maxDuration: 'settings.generation-modifiers.summary-max-duration',
  })

  const areas = [
    ...modifier.zones.map(zone => zone.name),
    ...modifier.countries.map(country =>
      countryName(country.isoCode, country.name)
    ),
  ].join(', ')
  const location = areas
    ? `${labels.matchMode[modifier.matchMode as GenerationModifierMatchMode]} ${
        labels.locationTest[
          modifier.locationTest as GenerationModifierLocationTest
        ]
      }: ${areas}`
    : null
  const scope = [modifier.users.map(user => user.fullName).join(', ')].filter(
    Boolean
  )

  const conditions = [
    ...scope,
    location,
    modifier.restTypes
      .map(restType => labels.restType[restType as RestType])
      .join(', '),
    modifier.minDurationHours !== null
      ? `${translations.minDuration} ${modifier.minDurationHours} ${translations.hours}`
      : null,
    modifier.maxDurationHours !== null
      ? `${translations.maxDuration} ${modifier.maxDurationHours} ${translations.hours}`
      : null,
    modifier.validFrom ? `${translations.from} ${modifier.validFrom}` : null,
    modifier.validTo ? `${translations.until} ${modifier.validTo}` : null,
  ].filter(Boolean)

  const effects = [
    modifier.award
      ? labels.award[modifier.award as GenerationModifierAward]
      : null,
    modifier.amount !== null
      ? [
          modifier.amount,
          modifier.perDurationHours
            ? `${translations.per} ${modifier.perDurationHours} ${
                translations.hours
              } · ${
                labels.rounding[modifier.rounding as GenerationRuleRounding]
              }`
            : null,
          modifier.maxAmount !== null
            ? `(${translations.max} ${modifier.maxAmount})`
            : null,
        ]
          .filter(value => value !== null)
          .join(' ')
      : null,
    modifier.salaryCode?.name,
    modifier.chainAction
      ? labels.chainAction[
          modifier.chainAction as GenerationModifierChainAction
        ]
      : null,
  ].filter(Boolean)

  return (
    <div className="flex min-w-0 flex-col">
      <span className="font-medium">
        {modifier.name}
        <span className="text-secondary ml-2 text-xs font-normal">
          {labels.kind[modifier.kind as GenerationRuleKind]} ·{' '}
          {labels.subject[modifier.appliesTo as GenerationModifierSubject]} ·{' '}
          {translations.priority} {modifier.priority}
        </span>
      </span>
      <span className="text-secondary text-sm">
        {conditions.join(' · ')}
        {conditions.length > 0 && effects.length > 0 && ' → '}
        {effects.join(' · ')}
      </span>
    </div>
  )
}
